import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ChevronRight, ChevronLeft, CalendarCheck, FileText, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { useModal } from "../../context/ModalContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { DateField } from "../DateField";
import { SelectField } from "../SelectField";
import { ResponsiveModal } from "../ResponsiveModal";

const EXPERIENCE = ["Complete beginner", "Some experience", "Test-ready / refresher"];

const HONEYPOT = { position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 };
const Honeypot = ({ value, onChange }) => (
  <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" style={HONEYPOT} value={value} onChange={onChange} />
);

export const LESSON_TYPES = ["Automatic", "Manual", "Refresher", "Pass Plus", "Motorway", "Mock Test"];
export const AREAS = ["Northolt", "Greenford", "Southall", "South Harrow"];
export const PACKAGES = [
  { name: "Automatic Lessons", price: "£317.95", note: "Most popular" },
  { name: "Manual Lessons", price: "£264.95", note: "Best value" },
];

const field = "w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 transition";

function Shell({ title, subtitle, icon: Icon, onClose, children }) {
  return (
    <ResponsiveModal title={title} description={subtitle} onClose={onClose}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="sticky top-0 glass border-b border-black/5 px-4 py-3 sm:px-6 sm:py-4 flex items-center gap-3 rounded-t-3xl z-10">
          <span className="brand-gradient text-white w-10 h-10 rounded-xl flex items-center justify-center shrink-0"><Icon size={18} /></span>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-base sm:text-lg leading-tight">{title}</h3>
            {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 text-neutral-500 hover:text-neutral-900 shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange" aria-label="Close" data-modal-initial-focus data-testid="modal-close"><X size={22} /></button>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </ResponsiveModal>
  );
}

function Success({ name, onClose, label }) {
  return (
    <div className="text-center py-6" data-testid="modal-success">
      <motion.div className="mx-auto w-20 h-20 rounded-full brand-gradient flex items-center justify-center text-white mb-5"
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}>
        <Check size={40} strokeWidth={3} />
      </motion.div>
      <h3 className="font-heading text-2xl font-bold">Thank you{name ? `, ${name.split(" ")[0]}` : ""}!</h3>
      <p className="text-neutral-500 mt-2">{label} We'll be in touch very shortly.</p>
      <button className="btn-primary mt-6 mx-auto" onClick={onClose} data-testid="success-close">Done</button>
    </div>
  );
}

function QuoteForm({ prefill, onClose }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", lesson_type: prefill.lesson_type || "", area: prefill.area || "", message: "", company: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/quotes", f); setDone(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.message || err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) return <Success name={f.name} onClose={onClose} label="Your free quote request is in." />;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="quote-form">
      <Honeypot value={f.company} onChange={set("company")} />
      <input className={field} placeholder="Full name *" required value={f.name} onChange={set("name")} data-testid="quote-name" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className={field} placeholder="Phone *" required value={f.phone} onChange={set("phone")} data-testid="quote-phone" />
        <input className={field} type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="quote-email" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <SelectField value={f.lesson_type} onChange={(v) => setF({ ...f, lesson_type: v })} options={LESSON_TYPES} placeholder="Lesson type" testId="quote-lesson" />
        <SelectField value={f.area} onChange={(v) => setF({ ...f, area: v })} options={AREAS} placeholder="Area" isSearchable testId="quote-area" />
      </div>
      <textarea className={field} rows={3} placeholder="Tell us about your experience or preferred times" value={f.message} onChange={set("message")} data-testid="quote-message" />
      <button className="btn-primary w-full" disabled={busy} data-testid="quote-submit">{busy ? "Sending..." : "Get My Free Quote"}</button>
      <p className="text-center text-xs text-neutral-400">No hidden charges · Flexible lesson timings</p>
    </form>
  );
}

function CallbackForm({ onClose }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", area: "", preferred_time: "", company: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/callbacks", f); setDone(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.message || err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) return <Success name={f.name} onClose={onClose} label="We've got your callback request." />;
  return (
      <form onSubmit={submit} className="space-y-3" data-testid="callback-form">
        <Honeypot value={f.company} onChange={set("company")} />
        <input className={field} placeholder="Full name *" required value={f.name} onChange={set("name")} data-testid="callback-name" />
        <input className={field} placeholder="Phone number *" required value={f.phone} onChange={set("phone")} data-testid="callback-phone" />
        <input className={field} type="email" placeholder="Email for confirmation" value={f.email} onChange={set("email")} data-testid="callback-email" />
        <SelectField value={f.area} onChange={(v) => setF({ ...f, area: v })} options={AREAS} placeholder="Area" isSearchable testId="callback-area" />
        <input className={field} placeholder="Best time to call (e.g. Weekday mornings)" value={f.preferred_time} onChange={set("preferred_time")} data-testid="callback-time" />
      <button className="btn-primary w-full" disabled={busy} data-testid="callback-submit">{busy ? "Sending..." : "Request My Callback"}</button>
    </form>
  );
}

function BookingForm({ prefill, onClose }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    lesson_type: prefill.lesson_type || "", area: prefill.area || "", package: prefill.package || "",
    preferred_datetime: "", name: "", phone: "", email: "", experience: "", message: "", company: "",
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    setBusy(true);
    try { await api.post("/bookings", f); setDone(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.message || err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) return <Success name={f.name} onClose={onClose} label="Your lesson booking is confirmed pending a quick call." />;

  const canNext = { 1: f.lesson_type, 2: f.area, 3: f.package, 4: f.preferred_datetime }[step];

  return (
    <div data-testid="booking-form">
      <div className="flex gap-1.5 mb-5">
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "brand-gradient" : "bg-neutral-200"}`} />
        ))}
      </div>

      {step === 1 && (
        <div>
          <p className="font-heading font-semibold mb-3">Choose your lesson type</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {LESSON_TYPES.map((l) => (
              <button key={l} onClick={() => pick("lesson_type", l)} className={`rounded-xl border px-4 py-3 text-sm font-semibold transition ${f.lesson_type === l ? "border-brand-orange bg-orange-50 text-brand-red" : "border-neutral-200 hover:border-neutral-300"}`} data-testid={`booking-lesson-${l.toLowerCase().replace(/ /g,"-")}`}>{l}</button>
            ))}
          </div>
        </div>
      )}
      {step === 2 && (
        <div>
          <p className="font-heading font-semibold mb-3">Which area are you in?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {AREAS.map((a) => (
              <button key={a} onClick={() => pick("area", a)} className={`rounded-xl border px-4 py-3 text-sm font-semibold transition ${f.area === a ? "border-brand-orange bg-orange-50 text-brand-red" : "border-neutral-200 hover:border-neutral-300"}`} data-testid={`booking-area-${a.toLowerCase().replace(/ /g,"-")}`}>{a}</button>
            ))}
          </div>
        </div>
      )}
      {step === 3 && (
        <div>
          <p className="font-heading font-semibold mb-3">Select a package</p>
          <div className="space-y-2">
            {PACKAGES.map((p) => (
              <button key={p.name} onClick={() => pick("package", p.name)} className={`w-full flex items-center justify-between rounded-xl border px-4 py-4 text-left transition ${f.package === p.name ? "border-brand-orange bg-orange-50" : "border-neutral-200 hover:border-neutral-300"}`} data-testid={`booking-package-${p.name.toLowerCase().replace(/ /g,"-")}`}>
                <span><span className="font-semibold block">{p.name}</span><span className="text-xs text-brand-orange font-semibold">{p.note}</span></span>
                <span className="font-heading font-bold text-lg">{p.price}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {step === 4 && (
        <div>
          <p className="font-heading font-semibold mb-3">Preferred date</p>
          <DateField value={f.preferred_datetime} onChange={(v) => setF({ ...f, preferred_datetime: v })} placeholder="Select Date" testId="booking-date" />
          <div className="mt-3">
            <SelectField value={f.experience} onChange={(v) => setF({ ...f, experience: v })} options={EXPERIENCE} placeholder="Your experience" testId="booking-experience" />
          </div>
        </div>
      )}
      {step === 5 && (
        <div className="space-y-3">
          <Honeypot value={f.company} onChange={set("company")} />
          <p className="font-heading font-semibold">Your contact details</p>
          <input className={field} placeholder="Full name *" required value={f.name} onChange={set("name")} data-testid="booking-name" />
          <input className={field} placeholder="Phone *" required value={f.phone} onChange={set("phone")} data-testid="booking-phone" />
          <input className={field} type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="booking-email" />
          <textarea className={field} rows={2} placeholder="Anything else we should know?" value={f.message} onChange={set("message")} data-testid="booking-notes" />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        {step > 1 && <button onClick={() => setStep(step - 1)} className="btn-outline w-full sm:w-auto" data-testid="booking-back"><ChevronLeft size={16} /> Back</button>}
        {step < 5 && <button onClick={() => setStep(step + 1)} disabled={!canNext} className="btn-primary flex-1 disabled:opacity-40" data-testid="booking-next">Continue <ChevronRight size={16} /></button>}
        {step === 5 && <button onClick={submit} disabled={busy || !f.name || !f.phone} className="btn-primary flex-1 disabled:opacity-40" data-testid="booking-confirm">{busy ? "Booking..." : "Confirm Booking"}</button>}
      </div>
    </div>
  );
}

export function Modals() {
  const { modal, prefill, close } = useModal();

  return (
    <AnimatePresence>
      {modal === "quote" && (
        <Shell key="q" title="Get a Free Quote" subtitle="No obligation · 60-second form" icon={FileText} onClose={close}>
          <QuoteForm prefill={prefill} onClose={close} />
        </Shell>
      )}
      {modal === "booking" && (
        <Shell key="b" title="Book Your Lesson" subtitle="5 quick steps" icon={CalendarCheck} onClose={close}>
          <BookingForm prefill={prefill} onClose={close} />
        </Shell>
      )}
      {modal === "callback" && (
        <Shell key="c" title="Request a Callback" subtitle="We'll call you back today" icon={PhoneCall} onClose={close}>
          <CallbackForm onClose={close} />
        </Shell>
      )}
    </AnimatePresence>
  );
}
